import { Grid } from '@mui/material';
import { RoutalLoader, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect } from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip } from 'recharts';
import styled from 'styled-components';

import useRetrieveAnalyticsSatisfaction from '../../hooks/analytics/customers/useRetrieveAnalyticsSatisfaction';
import useRetrieveAnalyticsDeliveryAttempts from '../../hooks/analytics/drivers/useRetrieveAnalyticsDeliveryAttempts';
import useRetrieveAnalyticsTimeOnRoute from '../../hooks/analytics/drivers/useRetrieveAnalyticsTimeOnRoute';
import useRetrieveAnalyticsAllStops from '../../hooks/analytics/planner/useRetrieveAnalyticsAllStops';
import useRetrieveAnalyticsCancellationReasons from '../../hooks/analytics/planner/useRetrieveAnalyticsCancellationReasons';
import { useRetrieveAnalyticsPlans } from '../../hooks/analytics/planner/useRetrieveAnalyticsPlans';
import { useAnalyticsFilters } from '../../hooks/analytics/useAnalyticsFilters';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import { useRetrieveFlatVehicles } from '../../hooks/vehicle/useRetrieveFlatVehicles';

import { useRetrieveUserCredentials } from '../../hooks/user/useRetrieveUserCredentials';
import Text from '../../react-components/Text';
import eventsPanel, { PageViewPages } from '../../services/events';
import {
  CustomersSatisfactionChartCustomTooltip,
  getVehiclesAll as getCustomersSatisfactionAllVehicles,
} from './customers/ProjectAnalyticsCustomersSatisfaction';
import {
  DeliveryAttemptsChartCustomTooltip,
  getVehiclesAll as getAttemptsAllVehicles,
} from './drivers/ProjectAnalyticsDriversDeliveryAttempts';
import {
  getVehiclesAll as getTimeOnRouteAllVehicles,
  RouteOnTimeChartCustomTooltip,
} from './drivers/ProjectAnalyticsDriversTimeOnRoute';
import { AllStopsChartCustomTooltip } from './planner/ProjectAnalyticsPlannerAllStops';
import { CancellationReasonsChartCustomTooltip } from './planner/ProjectAnalyticsPlannerCancellationReasons';
import { PlansChartCustomTooltip } from './planner/ProjectAnalyticsPlannerPlans';
import { cancelReasonColors } from './shared/analytics-colors';
import AnalyticsHeader from './shared/AnalyticsHeader';
import { AnalyticsPermissions } from './shared/AnalyticsPermissions';

const StyledChartContainer = styled(Grid)`
  border: 1px solid #e0e0e0;
  min-width: 600px;
  width: calc(45% - 24px);
  background-color: white;
  padding: 20px 24px;

  @media (max-width: 1440px) {
    min-width: 350px;
    width: calc(45% - 12px);
  }
`;

const ProjectAnalyticsSummary = () => {
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: projectVehicles } = useRetrieveFlatVehicles({ project: activeProject });
  const { binType, startDate, endDate } = useAnalyticsFilters({ project: activeProject });
  const { data: plansData, isLoading: plansIsLoading } = useRetrieveAnalyticsPlans({ startDate, endDate, binType });
  const { data: allStopsData, isLoading: allStopsIsLoading } = useRetrieveAnalyticsAllStops({
    startDate,
    endDate,
    binType,
  });
  const { data: cancellationReasons, isLoading: cancellationReasonsIsLoading } =
    useRetrieveAnalyticsCancellationReasons({
      startDate,
      endDate,
      binType,
      vehicleId: undefined,
    });
  const { data: timeOnRouteData, isLoading: timeOnRouteIsLoading } = useRetrieveAnalyticsTimeOnRoute({
    startDate,
    endDate,
    binType,
    vehicles: [],
  });
  const { data: deliveryAttemptsData, isLoading: deliveryAttemptsIsLoading } = useRetrieveAnalyticsDeliveryAttempts({
    startDate,
    endDate,
    binType,
    vehicles: [],
  });
  const { data: customersSatisfactionData, isLoading: customersSatisfactionIsLoading } =
    useRetrieveAnalyticsSatisfaction({
      startDate,
      endDate,
      binType,
      vehicles: [],
    });

  useEffect(() => {
    if (userCredentials?.subscription.test(`planner.analytics`)) {
      eventsPanel.pageView(PageViewPages.analytics.summary);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  if (!userCredentials?.subscription.test(`planner.analytics`)) {
    return <AnalyticsPermissions />;
  }

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{ width: `100%`, height: ` 100%`, backgroundColor: `#F6F6F6` }}
    >
      <AnalyticsHeader titleIntlId="project_analytics.summary.title" />

      <Grid
        container
        direction="row"
        justifyContent="space-around"
        style={{
          padding: `16px 22px`,
          backgroundColor: `#F6F6F6`,
          gridGap: `12px`,
          width: `100%`,
        }}
      >
        <StyledChartContainer>
          <Text variant="h6" isBold intlId="project_analytics.summary.plans.title" />

          <VSpacer medium />

          <div style={{ width: `100%`, height: 300 }}>
            {plansIsLoading ? (
              <Grid container alignItems="center" justifyContent="center" style={{ height: `100%` }}>
                <RoutalLoader width={60} />
              </Grid>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={plansData}
                  margin={{
                    top: 0,
                    right: 0,
                    left: 0,
                    bottom: 0,
                  }}
                  style={{
                    border: `1px solid #E0E0E0`,
                    borderTop: `none`,
                  }}
                >
                  <CartesianGrid vertical={false} />
                  <Tooltip content={<PlansChartCustomTooltip />} />
                  <Bar type="linear" dataKey="finished" stackId="1" fill={RoutalPalette.statusColors.completed} />
                  <Bar type="linear" dataKey="in_progress" stackId="1" fill={RoutalPalette.statusColors.canceled} />
                  <Bar type="linear" dataKey="planning" stackId="1" fill={RoutalPalette.statusColors.incomplete} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </StyledChartContainer>

        <StyledChartContainer>
          <Text variant="h6" isBold intlId="project_analytics.summary.all_stops.title" />

          <VSpacer medium />

          <div style={{ width: `100%`, height: 300 }}>
            {allStopsIsLoading ? (
              <Grid container alignItems="center" justifyContent="center" style={{ height: `100%` }}>
                <RoutalLoader width={60} />
              </Grid>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={allStopsData}
                  margin={{
                    top: 0,
                    right: 0,
                    left: 0,
                    bottom: 0,
                  }}
                  style={{
                    border: `1px solid #E0E0E0`,
                    borderBottom: `none`,
                  }}
                >
                  <CartesianGrid vertical={false} />
                  <Tooltip content={<AllStopsChartCustomTooltip />} />
                  <Area
                    type="linear"
                    dataKey="pending"
                    stroke={RoutalPalette.statusColors.pending}
                    fill={RoutalPalette.statusColors.pending}
                    stackId={1}
                    activeDot={{ r: 3, stroke: RoutalPalette.statusColors.pending, strokeWidth: 2, fill: `#FFF` }}
                  />
                  <Area
                    type="linear"
                    dataKey="completed"
                    stroke={RoutalPalette.statusColors.completed}
                    fill={RoutalPalette.statusColors.completed}
                    stackId={1}
                    activeDot={{ r: 3, stroke: RoutalPalette.statusColors.completed, strokeWidth: 2, fill: `#FFF` }}
                  />
                  <Area
                    type="linear"
                    dataKey="incomplete"
                    stroke={RoutalPalette.statusColors.incomplete}
                    fill={RoutalPalette.statusColors.incomplete}
                    stackId={1}
                    activeDot={{ r: 3, stroke: RoutalPalette.statusColors.incomplete, strokeWidth: 2, fill: `#FFF` }}
                  />
                  <Area
                    type="linear"
                    dataKey="canceled"
                    stroke={RoutalPalette.statusColors.canceled}
                    fill={RoutalPalette.statusColors.canceled}
                    stackId={1}
                    activeDot={{ r: 3, stroke: RoutalPalette.statusColors.canceled, strokeWidth: 2, fill: `#FFF` }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </StyledChartContainer>

        <StyledChartContainer>
          <Text variant="h6" isBold intlId="project_analytics.summary.cancellation_reasons.title" />

          <VSpacer medium />

          <div style={{ width: `100%`, height: 300 }}>
            {cancellationReasonsIsLoading ? (
              <Grid container alignItems="center" justifyContent="center" style={{ height: `100%` }}>
                <RoutalLoader width={60} />
              </Grid>
            ) : (
              <ResponsiveContainer>
                <AreaChart
                  data={cancellationReasons}
                  margin={{
                    top: 0,
                    right: 0,
                    left: 0,
                    bottom: 0,
                  }}
                  style={{
                    border: `1px solid #E0E0E0`,
                    borderTop: `none`,
                  }}
                >
                  <CartesianGrid vertical={false} />
                  <Tooltip content={<CancellationReasonsChartCustomTooltip />} />
                  <Area
                    type="linear"
                    dataKey="nobody"
                    stackId="1"
                    stroke={cancelReasonColors.nobody}
                    fill={cancelReasonColors.nobody}
                    activeDot={{ r: 3, stroke: cancelReasonColors.nobody, strokeWidth: 2, fill: `#FFF` }}
                  />
                  <Area
                    type="linear"
                    dataKey="wrong_address"
                    stackId="1"
                    stroke={cancelReasonColors.wrong_address}
                    fill={cancelReasonColors.wrong_address}
                    activeDot={{ r: 3, stroke: cancelReasonColors.wrong_address, strokeWidth: 2, fill: `#FFF` }}
                  />
                  <Area
                    type="linear"
                    dataKey="missing_information"
                    stackId="1"
                    stroke={cancelReasonColors.missing_information}
                    fill={cancelReasonColors.missing_information}
                    activeDot={{ r: 3, stroke: cancelReasonColors.missing_information, strokeWidth: 2, fill: `#FFF` }}
                  />
                  <Area
                    type="linear"
                    dataKey="other"
                    stackId="1"
                    stroke={cancelReasonColors.other}
                    fill={cancelReasonColors.other}
                    activeDot={{ r: 3, stroke: cancelReasonColors.other, strokeWidth: 2, fill: `#FFF` }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </StyledChartContainer>

        <StyledChartContainer>
          <Text variant="h6" isBold intlId="project_analytics.summary.time_on_route.title" />

          <VSpacer medium />

          <div style={{ width: `100%`, height: 300 }}>
            {timeOnRouteIsLoading ? (
              <Grid container alignItems="center" justifyContent="center" style={{ height: `100%` }}>
                <RoutalLoader width={60} />
              </Grid>
            ) : (
              <ResponsiveContainer>
                <BarChart
                  data={timeOnRouteData}
                  margin={{
                    top: 0,
                    right: 0,
                    left: 0,
                    bottom: 0,
                  }}
                >
                  <CartesianGrid vertical={false} />
                  <Tooltip content={<RouteOnTimeChartCustomTooltip projectVehicles={projectVehicles} />} />

                  <Bar
                    barSize={20}
                    dataKey={getTimeOnRouteAllVehicles().id}
                    fill={getTimeOnRouteAllVehicles().color?.value}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </StyledChartContainer>

        <StyledChartContainer>
          <Text variant="h6" isBold intlId="project_analytics.summary.delivery_attempts.title" />

          <VSpacer medium />

          <div style={{ width: `100%`, height: 300 }}>
            {deliveryAttemptsIsLoading ? (
              <Grid container alignItems="center" justifyContent="center" style={{ height: `100%` }}>
                <RoutalLoader width={60} />
              </Grid>
            ) : (
              <ResponsiveContainer>
                <BarChart
                  data={deliveryAttemptsData}
                  margin={{
                    top: 0,
                    right: 0,
                    left: 0,
                    bottom: 0,
                  }}
                >
                  <CartesianGrid vertical={false} />
                  <Tooltip content={<DeliveryAttemptsChartCustomTooltip projectVehicles={projectVehicles} />} />

                  <Bar
                    barSize={20}
                    dataKey={getAttemptsAllVehicles().id}
                    fill={getAttemptsAllVehicles().color?.value}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </StyledChartContainer>

        <StyledChartContainer>
          <Text variant="h6" isBold intlId="project_analytics.summary.satisfaction.title" />

          <VSpacer medium />

          <div style={{ width: `100%`, height: 300 }}>
            {customersSatisfactionIsLoading ? (
              <Grid container alignItems="center" justifyContent="center" style={{ height: `100%` }}>
                <RoutalLoader width={60} />
              </Grid>
            ) : (
              <ResponsiveContainer>
                <LineChart
                  data={customersSatisfactionData}
                  margin={{
                    top: 2,
                    right: 0,
                    left: 0,
                    bottom: 0,
                  }}
                  style={{
                    border: `1px solid #E0E0E0`,
                    borderTop: `none`,
                  }}
                >
                  <CartesianGrid vertical={false} />
                  <Tooltip content={<CustomersSatisfactionChartCustomTooltip projectVehicles={projectVehicles} />} />
                  <Line
                    type="linear"
                    dataKey={getCustomersSatisfactionAllVehicles().id}
                    stroke={getCustomersSatisfactionAllVehicles().color?.value}
                    dot={{ stroke: `none`, strokeWidth: 0, fill: getCustomersSatisfactionAllVehicles().color?.value }}
                    activeDot={{
                      r: 3,
                      stroke: getCustomersSatisfactionAllVehicles().color?.value,
                      strokeWidth: 2,
                      fill: `#FFF`,
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </StyledChartContainer>
      </Grid>
    </Grid>
  );
};

export default ProjectAnalyticsSummary;
